import React, { useState, useEffect } from "react";
import { EventsAPI, ParticipantsAPI } from "api_inbusiness";
import { useParams, useHistory } from "react-router-dom";
import { Typography, Button, Chip, Modal, IconButton, Tooltip } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ShareIcon from "@mui/icons-material/Share";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DOMPurify from "dompurify";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useUser } from "hooks/useUser";
import PeopleIcon from "@mui/icons-material/People";
import dayjs from "dayjs";
import SubscriptionUpsell from "./SubscriptionUpsell/SubscriptionUpsell";
import styles from "./Event.module.scss";

const Event = () => {
  const { id } = useParams();
  const navigate = useHistory();
  const { user, permissionsData } = useUser();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isParticipating, setIsParticipating] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [hasImage, setHasImage] = useState(false);
  const [expandDescription, setExpandDescription] = useState(false);

  // Get image URL
  const imageUrl = event?.id ? EventsAPI.getImageUrl(event.id) : null;

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    return dayjs(dateValue).format("DD MMMM YYYY");
  };

  // Format time
  const formatTime = (timeValue) => {
    if (!timeValue) return "";
    const newDate = new Date(timeValue);
    const options = { hour12: false, timeZone: "Europe/Bucharest" };
    return newDate.toLocaleTimeString("ro-RO", options).slice(0, 5);
  };

  // Check if event is past
  const isPast = () => {
    if (!event?.date) return false;
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  // Countdown timer state
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });

  // Calculate countdown
  useEffect(() => {
    if (!event?.date || isPast()) return;

    const calculateCountdown = () => {
      const eventDateTime = new Date(event.date);
      if (event.startTime) {
        const startTime = new Date(event.startTime);
        eventDateTime.setHours(startTime.getHours(), startTime.getMinutes());
      }
      
      const now = new Date();
      const diff = eventDateTime - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setCountdown({ days, hours, minutes });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [event]);

  // Share function using Web Share API with fallback
  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;

  const handleShare = async () => {
    const shareData = {
      title: event?.title || 'Eveniment InBusiness',
      text: `Vezi acest eveniment: ${event?.title}`,
      url: window.location.href,
    };

    if (canNativeShare) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error - silently ignore
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiat în clipboard!');
      } catch (err) {
        // Fallback fallback: open in new window
        const text = `${event?.title} - ${window.location.href}`;
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
      }
    }
  };

  // Fetch event data
  const getEvent = () => {
    setLoading(true);
    EventsAPI.publicGetById(id).then((res) => {
      if (res.ok) {
        setEvent(res.data);
      }
      setLoading(false);
    });
  };

  // Check if user is participating
  const checkParticipation = () => {
    if (user?.id) {
      ParticipantsAPI.getStatus(user.id, id).then((res) => {
        if (res.ok) {
          setIsParticipating(res.data);
        }
      });
    }
  };

  // Handle participate click
  const handleParticipate = () => {
    if (!user?.id) {
      toast.info("Trebuie să te autentifici pentru a participa la acest eveniment");
      navigate.push(`/login?redirect=/events/${id}`);
      return;
    }

    const objForParticipant = {
      userId: user.id,
      eventId: id,
      isAttending: true,
    };

    ParticipantsAPI.create(objForParticipant).then((res) => {
      if (res.ok) {
        setIsParticipating(true);
        toast.success("Prezență confirmată cu succes!");
        getEvent();
      } else {
        toast.error("A intervenit o eroare");
      }
    });
  };

  // Handle cancel participation
  const handleCancelParticipate = () => {
    if (!user?.id) return;

    ParticipantsAPI.cancelParticipation(user.id, id).then((res) => {
      if (res.ok) {
        setIsParticipating(false);
        toast.success("Participarea a fost anulată");
        getEvent();
      } else {
        toast.error("A intervenit o eroare");
      }
    });
  };

  // Sanitize HTML description
  const sanitizeHTML = (html) => {
    if (!html) return "";
    return DOMPurify.sanitize(html);
  };

  // Check if image exists
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => setHasImage(true);
      img.onerror = () => setHasImage(false);
      img.src = imageUrl;
    }
  }, [imageUrl]);

  // Handle scroll for mobile sticky CTA
  useEffect(() => {
    const handleScroll = () => {
      setShowMobileCta(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    getEvent();
  }, [id]);

  useEffect(() => {
    checkParticipation();
  }, [user]);

  const eventIsPast = isPast();

  return (
    <>
      <Helmet>
        <title>InBusiness CRM :: {event?.title || "Eveniment"}</title>
      </Helmet>
      
      <div className={styles.pageContainer}>
        {/* Full-Width Hero Banner */}
        <header className={styles.heroBanner}>
          <div className={styles.heroContent}>
            <Typography variant="h1" className={styles.heroTitle}>
              {event?.title}
            </Typography>
            {event?.subtitle && (
              <Typography variant="h5" className={styles.heroSubtitle}>
                {event?.subtitle}
              </Typography>
            )}
          </div>
        </header>

        {/* Two-Column Content Layout */}
        <div className={styles.contentLayout}>
          {/* Left Column - Image */}
          <div className={styles.imageColumn}>
            {hasImage && (
              <div 
                className={styles.imageWrapper}
                onClick={() => setImageModalOpen(true)}
              >
                <img 
                  src={imageUrl} 
                  alt={event?.title}
                  className={styles.eventImage}
                />
                <div className={styles.imageOverlay}>
                  <ZoomInIcon />
                  <span>Click pentru imagine completă</span>
                </div>

                {/* Countdown Badge - premium floating */}
                {!eventIsPast && (countdown.days > 0 || countdown.hours > 0) && (
                  <div className={styles.countdownBadge}>
                    <AccessTimeIcon className={styles.countdownIcon} />
                    <span>
                      {countdown.days > 0 
                        ? `${countdown.days}z ${countdown.hours}h`
                        : `${countdown.hours}h ${countdown.minutes}m`
                      }
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* Mobile: Info bar after image */}
            <div className={styles.mobileInfoBar}>
              <div className={styles.infoItem}>
                <CalendarTodayIcon />
                <span>{formatDate(event?.date)}</span>
              </div>
              <div className={styles.infoItem}>
                <AccessTimeIcon />
                <span>{formatTime(event?.startTime)}</span>
              </div>
              <div className={styles.infoItem}>
                <LocationOnIcon />
                <span>
                  {event?.county?.name && `${event.county.name}, `}
                  {event?.location || "Online"}
                </span>
              </div>

              {/* Mobile CTA */}
              <div className={styles.mobileCta}>
                {eventIsPast ? (
                  <Chip label="Eveniment finalizat" className={styles.pastChip} />
                ) : isParticipating ? (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="Participi" 
                    className={styles.participatingChip}
                  />
                ) : (
                  <Button 
                    variant="contained" 
                    fullWidth
                    className={styles.ctaButton}
                    onClick={handleParticipate}
                  >
                    VREAU SĂ PARTICIP
                  </Button>
                )}
              </div>

              {/* Mobile Share - single button */}
              <div className={styles.mobileShare}>
                <Button 
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  className={styles.shareBtn}
                  onClick={handleShare}
                >
                  Distribuie
                </Button>
              </div>
            </div>

            {/* Description - Under Image */}
            {event?.description && (
              <div className={styles.descriptionCard}>
                <Typography variant="h6" className={styles.cardTitle}>
                  Despre Eveniment
                </Typography>
                <div 
                  className={`${styles.descriptionContent} ${!expandDescription ? styles.collapsed : ''}`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(event.description) }}
                />
                {event.description.length > 500 && (
                  <Button
                    variant="text"
                    className={styles.readMoreButton}
                    onClick={() => setExpandDescription(!expandDescription)}
                  >
                    {expandDescription ? 'Arată mai puțin ↑' : 'Citește mai mult ↓'}
                  </Button>
                )}
              </div>
            )}

            {/* Mobile: Subscription Upsell */}
            <div className={styles.mobileUpsell}>
              <SubscriptionUpsell />
            </div>
          </div>

          {/* Right Column - Details */}
          <div className={styles.detailsColumn}>
            {/* Event Info Card */}
            <div className={styles.infoCard}>
              <Typography variant="h6" className={styles.cardTitle}>
                Detalii Eveniment
              </Typography>
              
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <CalendarTodayIcon />
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Data</span>
                    <span className={styles.infoValue}>{formatDate(event?.date)}</span>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <AccessTimeIcon />
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Ora</span>
                    <span className={styles.infoValue}>{formatTime(event?.startTime)}</span>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <LocationOnIcon />
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Locație</span>
                    <span className={styles.infoValue}>
                      {event?.county?.name && `${event.county.name}, `}
                      {event?.location || "Online"}
                    </span>
                  </div>
                </div>

                {/* Participants - Admin only */}
                {permissionsData?.seeAdminButtons && (
                  <div className={styles.infoItem}>
                    <PeopleIcon />
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Participanți</span>
                      <span className={styles.infoValue}>
                        {event?.participants?.length || 0} înscriși
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA Section */}
              <div className={styles.ctaSection}>
                {eventIsPast ? (
                  <Chip label="Eveniment finalizat" className={styles.pastChip} />
                ) : isParticipating ? (
                  <div className={styles.participatingState}>
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Participi la acest eveniment" 
                      className={styles.participatingChip}
                    />
                    <Button 
                      variant="text" 
                      size="small"
                      onClick={handleCancelParticipate}
                      className={styles.cancelLink}
                    >
                      Anulează participarea
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="contained" 
                    fullWidth
                    className={styles.ctaButton}
                    onClick={handleParticipate}
                  >
                    VREAU SĂ PARTICIP
                  </Button>
                )}
              </div>

              {/* Share Section - single button */}
              <div className={styles.shareSection}>
                <Button 
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  className={styles.shareBtn}
                  onClick={handleShare}
                >
                  Distribuie
                </Button>
              </div>
            </div>

            {/* Subscription Upsell */}
            <SubscriptionUpsell />
          </div>
        </div>

        {/* Mobile Sticky CTA */}
        <div className={`${styles.mobileStickyBar} ${showMobileCta ? styles.visible : ''}`}>
          <div className={styles.mobileCtaContent}>
            <div className={styles.mobileEventInfo}>
              <Typography variant="body2" className={styles.mobileTitle}>
                {event?.title?.length > 25 ? event.title.substring(0, 25) + '...' : event?.title}
              </Typography>
            </div>
            {!eventIsPast && !isParticipating && (
              <Button 
                variant="contained" 
                size="small"
                className={styles.mobileCtaButton}
                onClick={handleParticipate}
              >
                PARTICIP
              </Button>
            )}
            {isParticipating && !eventIsPast && (
              <Chip 
                icon={<CheckCircleIcon />}
                label="Înscris" 
                size="small"
                className={styles.mobileParticipatingChip}
              />
            )}
          </div>
        </div>

        {/* Full Image Modal */}
        <Modal
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          className={styles.imageModal}
        >
          <div className={styles.modalContent}>
            <IconButton 
              className={styles.closeButton}
              onClick={() => setImageModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <img 
              src={imageUrl} 
              alt={event?.title}
              className={styles.modalImage}
            />
          </div>
        </Modal>

        {/* Footer Attribution */}
        <div className={styles.logoFooter}>
          <a 
            href="https://raisissoftware.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.logoLink}
          >
            Infrastructură software dezvoltată de RaisisSoftware
          </a>
        </div>
      </div>
    </>
  );
};

export default Event;
